import { Controller, ControllerProps, FieldValues } from "react-hook-form";
import debounce from "lodash.debounce";
import { useRef } from "react";
import { TextInput } from "ui/atoms/input/TextInput";
import { View } from "ui/atoms/view";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from "ui/gluestack/form-control";
import { AlertCircleIcon, Icon, SearchIcon } from "ui/gluestack/icon";
export interface Props<TFieldValues extends FieldValues = FieldValues> {
  id: string;
  name: ControllerProps<TFieldValues>["name"];
  control: ControllerProps<TFieldValues>["control"];
  placeholder?: string;
  invalidMessage?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  autoFocus?: boolean;
  rules?: ControllerProps<TFieldValues>["rules"];
  className?: string;
  prefix?: React.ReactNode;
  postfix?: React.ReactNode;
  textInputClassName?: string;
  onDebounce?: (value?: string) => void;
  onSubmitEditing?: () => void;
}

export function FormControlledSearchInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  id,
  name,
  isInvalid,
  isReadOnly,
  isDisabled,
  isRequired,
  autoFocus,
  placeholder,
  invalidMessage,
  control,
  rules,
  className,
  prefix,
  postfix,
  textInputClassName,
  onSubmitEditing,
  onDebounce,
}: Props<TFieldValues>) {
  const debouncedSearch = useRef(debounce(onDebounce, 500));

  const onChangeText = (text: string) => {
    debouncedSearch.current(text);
  };
  return (
    <Controller
      control={control}
      name={name}
      render={({ fieldState: { error }, field: { value, onChange, name } }) => (
        <FormControl
          isInvalid={isInvalid || !!error}
          size="md"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          isRequired={isRequired}
        >
          <View className={`relative fill-white ${className}`}>
            {prefix}
            <TextInput
              id={id}
              name={name}
              value={value}
              onChange={(text) => {
                onChange(text);
                onChangeText(text);
              }}
              placeholder={placeholder}
              type={"text"}
              variant={undefined}
              onSubmitEditing={onSubmitEditing}
              autoFocus={autoFocus}
              className={`truncate ${textInputClassName}`}
              icon={
                <Icon as={SearchIcon} className="w-8 h-5 text-gray-500 px-2" />
              }
            />
            {postfix}
          </View>
          {invalidMessage || error?.message ? (
            <FormControlError>
              <FormControlErrorIcon
                as={AlertCircleIcon}
                className="text-error-500"
                size="sm"
              />
              <FormControlErrorText className="text-error-500 text-sm">
                {error?.message || invalidMessage}
              </FormControlErrorText>
            </FormControlError>
          ) : null}
        </FormControl>
      )}
      rules={rules}
    />
  );
}
